import React, { useState, useEffect } from 'react';
import { Plus, Building2, Users, CheckCircle, XCircle, Edit, Key, Eye, EyeOff, Lock, Unlock, Search, Filter, Copy, Check, Upload, Image, UserPlus, CreditCard } from 'lucide-react';
import axios from 'axios';
import { compressImage } from '@/utils/imageCompressor';
import { formatDate } from '../../utils/dateTimeUtils';

const GroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showCollegesModal, setShowCollegesModal] = useState(false);
  const [showSetupSubModal, setShowSetupSubModal] = useState(false);
  const [setupSubFormData, setSetupSubFormData] = useState({ plan_type: 'yearly', amount: '', max_colleges: '', max_students: '', max_staff: '', grace_period_days: 7, gst_percent: 18, gst_included: false });
  
  // Selected group for actions
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupCredentials, setGroupCredentials] = useState(null);
  const [newPassword, setNewPassword] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    logo: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    admin_name: '',
    admin_email: '',
    max_colleges: '',
    max_students: '',
    max_staff: '',
    subscription_plan: '',
    billing_cycle: 'yearly',
    subscription_end_date: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGroups();
    fetchSubscriptionPlans();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch GROUP subscription plans (not tenant subscription plans)
      const response = await axios.get(`${API_URL}/api/groups/subscription-plans/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Response is direct array
      setSubscriptionPlans(response.data || []);
    } catch (error) {
      console.error('Error fetching group subscription plans:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/groups/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter groups
  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.admin_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && group.status === 'active') ||
                         (statusFilter === 'blocked' && group.status === 'blocked');
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: groups.length,
    active: groups.filter(g => g.status === 'active').length,
    blocked: groups.filter(g => g.status === 'blocked' || g.status === 'inactive').length,
    totalColleges: groups.reduce((sum, g) => sum + (g.college_count || 0), 0)
  };

  // Handle Add Group
  const handleAddGroup = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/groups/create`, {
        ...formData,
        max_colleges: formData.max_colleges ? parseInt(formData.max_colleges) : 10,
        max_students: formData.max_students ? parseInt(formData.max_students) : -1,
        max_staff: formData.max_staff ? parseInt(formData.max_staff) : -1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const creds = res.data?.admin_credentials;
      if (creds) {
        // Show credentials so SuperAdmin can copy them in case email delivery fails
        alert(
          `Group created successfully!\n\n` +
          `Group Admin Credentials:\n` +
          `Email: ${creds.email}\n` +
          `Password: ${creds.password}\n\n` +
          `These credentials have also been emailed. Please save them — the password cannot be retrieved later (you can only reset it).`
        );
      } else {
        alert('Group created successfully! Please assign an admin from the actions menu.');
      }
      setShowAddModal(false);
      resetForm();
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit Group
  const handleEditGroup = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/groups/${selectedGroup.id}`, {
        name: formData.name || null,
        code: formData.code || null,
        description: formData.description || null,
        logo: formData.logo || null,
        contact_person: formData.contact_person || null,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        max_colleges: formData.max_colleges ? parseInt(formData.max_colleges) : null,
        max_students: formData.max_students ? parseInt(formData.max_students) : null,
        max_staff: formData.max_staff ? parseInt(formData.max_staff) : null,
        subscription_end_date: formData.subscription_end_date || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Group updated successfully!');
      setShowEditModal(false);
      setSelectedGroup(null);
      resetForm();
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update group');
    } finally {
      setActionLoading(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name || '',
      code: group.code || '',
      description: group.description || '',
      logo: group.logo || '',
      contact_person: group.contact_person || '',
      contact_email: group.contact_email || '',
      contact_phone: group.contact_phone || '',
      admin_name: group.admin_name || '',
      admin_email: group.admin_email || '',
      max_colleges: group.max_colleges?.toString() || '',
      max_students: group.subscription?.max_students?.toString() || '',
      max_staff: group.subscription?.max_staff?.toString() || '',
      subscription_plan: group.subscription?.plan_code || group.subscription?.plan_type || '',
      billing_cycle: group.subscription?.billing_cycle || 'yearly',
      subscription_end_date: group.subscription?.end_date?.split('T')[0] || ''
    });
    setLogoPreview(group.logo || null);
    setShowEditModal(true);
  };

  // View Credentials
  const handleViewCredentials = async (group) => {
    setSelectedGroup(group);
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/groups/${group.id}/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupCredentials(response.data);
      setShowCredentialsModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to fetch credentials');
    } finally {
      setActionLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = (group) => {
    setSelectedGroup(group);
    setNewPassword(null);
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/groups/${selectedGroup.id}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewPassword(response.data.new_password);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
      setShowResetPasswordModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  // View Colleges
  const handleViewColleges = (group) => {
    setSelectedGroup(group);
    setShowCollegesModal(true);
  };

  // Block/Unblock
  const handleToggleBlock = async (group) => {
    const action = group.status === 'active' ? 'block' : 'unblock';
    if (!window.confirm(`Are you sure you want to ${action} "${group.name}"? This will ${action} all colleges under this group.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/groups/${group.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} group`);
    }
  };

  // Open Setup Subscription Modal (generates first invoice automatically)
  const handleSetupSubscription = (group) => {
    setSelectedGroup(group);
    // Pre-fill from existing subscription if available
    const existingPlanType = group.subscription?.billing_cycle || group.subscription?.plan_type || 'yearly';
    setSetupSubFormData({
      plan_type: existingPlanType,
      amount: '',
      max_colleges: group.max_colleges?.toString() || '5',
      max_students: group.subscription?.max_students && group.subscription?.max_students !== -1 ? group.subscription.max_students.toString() : '',
      max_staff: group.subscription?.max_staff && group.subscription?.max_staff !== -1 ? group.subscription.max_staff.toString() : '',
      grace_period_days: 7,
      gst_percent: 18,
      gst_included: false,
    });
    setShowSetupSubModal(true);
  };

  const handleSetupSubscriptionSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(setupSubFormData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/groups/setup-subscription/${selectedGroup.id}`,
        {
          plan_type: setupSubFormData.plan_type,
          amount,
          currency: 'INR',
          max_colleges: parseInt(setupSubFormData.max_colleges) || 5,
          max_students: setupSubFormData.max_students === '' ? -1 : (parseInt(setupSubFormData.max_students) || -1),
          max_staff: setupSubFormData.max_staff === '' ? -1 : (parseInt(setupSubFormData.max_staff) || -1),
          grace_period_days: parseInt(setupSubFormData.grace_period_days) || 7,
          gst_percent: parseFloat(setupSubFormData.gst_percent) || 0,
          gst_included: !!setupSubFormData.gst_included,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const inv = res.data?.invoice;
      const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
      alert(
        `Subscription setup successful!\n\n` +
        (inv
          ? `Invoice: ${inv.invoice_number}\n` +
            `Base Amount: ₹${fmt(inv.base_amount)}\n` +
            `GST (${inv.gst_percent || 0}%): ₹${fmt(inv.gst_amount)}\n` +
            `Total Amount: ₹${fmt(inv.amount)}\n` +
            `Due: ${inv.due_date}\n\n`
          : '') +
        `Go to Subscriptions page to mark as paid once payment is received.`
      );
      setShowSetupSubModal(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to setup subscription');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Assign admin to a group that has no admin yet (repair existing groups
  // that were created BEFORE the unified create flow was added).
  const handleAssignAdmin = async (group) => {
    const adminName = window.prompt(`Assign Admin for "${group.name}"\n\nEnter Admin Full Name:`);
    if (!adminName) return;
    const adminEmail = window.prompt('Enter Admin Email:');
    if (!adminEmail) return;
    const adminPhone = window.prompt('Enter Admin Phone (10 digits):');
    if (!adminPhone) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/groups/${group.id}/create-admin`, {
        name: adminName,
        email: adminEmail,
        phone: adminPhone,
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const creds = res.data?.credentials;
      if (creds) {
        alert(
          `Admin assigned successfully!\n\n` +
          `Email: ${creds.email}\n` +
          `Password: ${creds.password}\n\n` +
          `Credentials have also been emailed. Please save these — they cannot be retrieved later.`
        );
      } else {
        alert('Admin assigned successfully!');
      }
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign admin');
    }
  };
  
  // Helpers
  const resetForm = () => {
    setFormData({
      name: '', code: '', description: '', logo: '',
      contact_person: '', contact_email: '', contact_phone: '',
      admin_name: '', admin_email: '',
      max_colleges: '', max_students: '', max_staff: '',
      subscription_plan: '', billing_cycle: 'yearly', subscription_end_date: ''
    });
    setLogoPreview(null);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo size must be less than 5MB');
        return;
      }
      try {
        const result = await compressImage(file, { maxDim: 400, quality: 0.85 });
        setLogoPreview(result.dataUrl);
        setFormData({ ...formData, logo: result.dataUrl });
        console.log(`Logo compressed: ${result.width}x${result.height}, ~${result.sizeKB}KB`);
      } catch (err) {
        alert('Failed to process logo: ' + err.message);
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>;
    }
    return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Blocked</span>;
  };

  const getPlanBadge = (plan) => {
    const colors = {
      monthly: 'bg-blue-100 text-blue-700',
      yearly: 'bg-purple-100 text-purple-700',
      lifetime: 'bg-amber-100 text-amber-700'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[plan] || 'bg-gray-100 text-gray-700'}`}>
      {(plan || 'N/A').toUpperCase()}
    </span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Group Management</h1>
          <p className="text-gray-600 mt-1">Manage Group of Colleges</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          Add Group
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Groups</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Groups</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Blocked Groups</p>
              <p className="text-2xl font-bold text-red-600">{stats.blocked}</p>
            </div>
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Colleges</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalColleges}</p>
            </div>
            <Building2 className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, code, or admin email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Groups Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Group</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Code</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Admin</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Colleges</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Plan</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Validity</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Status</th>
              <th className="text-left px-3 py-3 whitespace-nowrap font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredGroups.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-gray-500">
                  {searchTerm || statusFilter !== 'all' ? 'No groups match your search' : 'No groups created yet'}
                </td>
              </tr>
            ) : (
              filteredGroups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {group.logo ? (
                        <img src={group.logo} alt={group.name} className="h-10 w-10 object-contain rounded border" />
                      ) : (
                        <Users className="h-10 w-10 text-purple-400" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{group.name}</p>
                        {group.contact_email && (
                          <p className="text-xs text-gray-500">{group.contact_email}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <code className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-sm font-semibold">
                      {group.code || '-'}
                    </code>
                  </td>
                  <td className="px-3 py-3">
                    <p className="text-sm text-gray-900">{group.admin_name || '-'}</p>
                    <p className="text-xs text-gray-500">{group.admin_email || '-'}</p>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-purple-600">{group.college_count || 0}</span>
                      <span className="text-xs text-gray-500">/ {group.max_colleges || '∞'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">{getPlanBadge(group.subscription?.plan_type)}</td>
                  <td className="px-3 py-3">
                    <p className="text-sm">
                      {group.subscription?.end_date ? formatDate(group.subscription.end_date) : '-'}
                    </p>
                  </td>
                  <td className="px-3 py-3">{getStatusBadge(group.status)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-0.5 flex-nowrap">
                      <button
                        onClick={() => openEditModal(group)}
                        className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleViewColleges(group)}
                        className="p-1.5 hover:bg-purple-50 rounded text-purple-600"
                        title="View Colleges"
                      >
                        <Building2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleViewCredentials(group)}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                        title="View Credentials"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleSetupSubscription(group)}
                        className="p-1.5 hover:bg-green-50 rounded text-green-600"
                        title="Setup Subscription / Generate Invoice"
                        data-testid={`setup-subscription-btn-${group.id}`}
                      >
                        <CreditCard className="h-4 w-4" />
                      </button>
                      {group.admin_email ? (
                        <button
                          onClick={() => handleResetPassword(group)}
                          className="p-1.5 hover:bg-yellow-50 rounded text-yellow-600"
                          title="Reset Admin Password"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAssignAdmin(group)}
                          className="p-1.5 hover:bg-orange-50 rounded text-orange-600 animate-pulse"
                          title="Assign Admin (no admin yet)"
                          data-testid={`assign-admin-btn-${group.id}`}
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleBlock(group)}
                        className={`p-1.5 hover:bg-gray-100 rounded ${group.status === 'active' ? 'text-red-600' : 'text-green-600'}`}
                        title={group.status === 'active' ? 'Block Group' : 'Unblock Group'}
                      >
                        {group.status === 'active' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Setup Subscription Modal */}
      {showSetupSubModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-600" />
                Setup Subscription
              </h2>
              <p className="text-sm text-gray-500">{selectedGroup.name}</p>
            </div>
            <form onSubmit={handleSetupSubscriptionSubmit}>
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  This will create a subscription record and immediately generate the first invoice on the Subscriptions page so you can mark it paid once payment is received.
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Plan Type *</label>
                  <select
                    value={setupSubFormData.plan_type}
                    onChange={(e) => setSetupSubFormData({ ...setupSubFormData, plan_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="setup-sub-plan-type"
                    required
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {setupSubFormData.gst_included ? 'Total Amount (incl. GST) *' : 'Base Amount (excl. GST) *'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={setupSubFormData.amount}
                    onChange={(e) => setSetupSubFormData({ ...setupSubFormData, amount: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="50000"
                    data-testid="setup-sub-amount"
                    required
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {setupSubFormData.gst_included ? 'Enter total amount; GST will be extracted from it.' : 'Enter base amount; GST will be added on top.'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">GST %</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={setupSubFormData.gst_percent}
                      onChange={(e) => setSetupSubFormData({ ...setupSubFormData, gst_percent: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      data-testid="setup-sub-gst-percent"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 px-3 py-2 cursor-pointer w-full">
                      <input
                        type="checkbox"
                        checked={setupSubFormData.gst_included}
                        onChange={(e) => setSetupSubFormData({ ...setupSubFormData, gst_included: e.target.checked })}
                        className="w-4 h-4 text-blue-600"
                        data-testid="setup-sub-gst-included"
                      />
                      <span className="text-sm">GST included in amount</span>
                    </label>
                  </div>
                </div>

                {/* Live GST Breakdown */}
                {(() => {
                  const entered = parseFloat(setupSubFormData.amount) || 0;
                  const gstPct = parseFloat(setupSubFormData.gst_percent) || 0;
                  if (entered <= 0) return null;
                  let baseAmt, gstAmt, totalAmt;
                  if (gstPct > 0) {
                    if (setupSubFormData.gst_included) {
                      baseAmt = +(entered / (1 + gstPct / 100)).toFixed(2);
                      gstAmt = +(entered - baseAmt).toFixed(2);
                      totalAmt = entered;
                    } else {
                      baseAmt = entered;
                      gstAmt = +(entered * gstPct / 100).toFixed(2);
                      totalAmt = +(entered + gstAmt).toFixed(2);
                    }
                  } else {
                    baseAmt = entered;
                    gstAmt = 0;
                    totalAmt = entered;
                  }
                  const fmt = (n) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                  return (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 space-y-1">
                      <div className="flex justify-between text-sm"><span className="text-gray-600">Base Amount:</span><span className="font-medium">₹{fmt(baseAmt)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-600">GST ({gstPct}%):</span><span className="font-medium">₹{fmt(gstAmt)}</span></div>
                      <div className="flex justify-between text-base pt-1 border-t border-green-200"><span className="font-semibold text-gray-800">Total Amount:</span><span className="font-bold text-green-700" data-testid="setup-sub-total-preview">₹{fmt(totalAmt)}</span></div>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Colleges</label>
                    <input
                      type="number"
                      min="1"
                      value={setupSubFormData.max_colleges}
                      onChange={(e) => setSetupSubFormData({ ...setupSubFormData, max_colleges: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Grace Period (days)</label>
                    <input
                      type="number"
                      min="0"
                      value={setupSubFormData.grace_period_days}
                      onChange={(e) => setSetupSubFormData({ ...setupSubFormData, grace_period_days: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Students</label>
                    <input
                      type="number"
                      value={setupSubFormData.max_students}
                      onChange={(e) => setSetupSubFormData({ ...setupSubFormData, max_students: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="2200 (blank = Unlimited)"
                      data-testid="setup-sub-max-students"
                    />
                    <p className="text-xs text-gray-500 mt-1">Blank or -1 = Unlimited</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Staff</label>
                    <input
                      type="number"
                      value={setupSubFormData.max_staff}
                      onChange={(e) => setSetupSubFormData({ ...setupSubFormData, max_staff: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="300 (blank = Unlimited)"
                      data-testid="setup-sub-max-staff"
                    />
                    <p className="text-xs text-gray-500 mt-1">Blank or -1 = Unlimited</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t flex gap-3">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  data-testid="setup-sub-submit-btn"
                >
                  {actionLoading ? 'Generating Invoice...' : 'Setup & Generate Invoice'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowSetupSubModal(false); setSelectedGroup(null); }}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Colleges Modal */}
      {showCollegesModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Colleges under {selectedGroup.name}</h2>
              <p className="text-sm text-gray-500">{selectedGroup.college_count || 0} colleges</p>
            </div>
            <div className="p-6">
              {selectedGroup.colleges && selectedGroup.colleges.length > 0 ? (
                <div className="space-y-3">
                  {selectedGroup.colleges.map((college, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-8 w-8 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{college.name}</p>
                          <p className="text-xs text-gray-500">{college.subdomain}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        college.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {college.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No colleges under this group yet</p>
              )}
              <button
                onClick={() => { setShowCollegesModal(false); setSelectedGroup(null); }}
                className="w-full mt-4 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && groupCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Group Admin Credentials</h2>
              <p className="text-sm text-gray-500">{selectedGroup?.name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Email</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded">{groupCredentials.email}</code>
                  <button
                    onClick={() => copyToClipboard(groupCredentials.email)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Password</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded">
                    {showPassword ? groupCredentials.password : '••••••••'}
                  </code>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(groupCredentials.password)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button
                onClick={() => { setShowCredentialsModal(false); setGroupCredentials(null); setShowPassword(false); }}
                className="w-full mt-4 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Reset Group Admin Password</h2>
              <p className="text-sm text-gray-500">{selectedGroup?.name}</p>
            </div>
            <div className="p-6">
              {newPassword ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700 font-medium mb-2">Password Reset Successfully!</p>
                    <p className="text-sm text-gray-600 mb-2">New Password:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border font-mono">{newPassword}</code>
                      <button
                        onClick={() => copyToClipboard(newPassword)}
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowResetPasswordModal(false); setNewPassword(null); }}
                    className="w-full px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-600">Are you sure you want to reset the admin password for this group? A new password will be generated.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={confirmResetPassword}
                      disabled={actionLoading}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading ? 'Resetting...' : 'Yes, Reset Password'}
                    </button>
                    <button
                      onClick={() => setShowResetPasswordModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Group Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Create New Group</h2>
              <p className="text-sm text-gray-500">Add a new Group of Colleges</p>
            </div>
            <form onSubmit={handleAddGroup}>
              <div className="p-6 space-y-4">
                {/* Logo Upload */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="h-20 w-20 object-contain rounded-lg border" />
                    ) : (
                      <div className="h-20 w-20 rounded-lg border-2 border-dashed flex items-center justify-center text-gray-400">
                        <Image className="h-8 w-8" />
                      </div>
                    )}
                    <label className="absolute bottom-0 right-0 bg-blue-600 p-1 rounded-full cursor-pointer">
                      <Upload className="h-3 w-3 text-white" />
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p className="font-medium">Group Logo</p>
                    <p>Max 2MB, PNG/JPG</p>
                  </div>
                </div>

                {/* Group Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Group Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="ABC Education Trust"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Group Code *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border rounded-lg uppercase"
                      placeholder="ABCEDU"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                    placeholder="Brief description of the group"
                  />
                </div>

                {/* Contact Details */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Contact Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Person *</label>
                      <input
                        type="text"
                        value={formData.contact_person}
                        onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Email *</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="contact@abcedu.com"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Contact Phone *</label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="9876543210"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Admin Details */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Group Admin (will be created after group)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Admin Name *</label>
                      <input
                        type="text"
                        value={formData.admin_name}
                        onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Admin Name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Admin Email *</label>
                      <input
                        type="email"
                        value={formData.admin_email}
                        onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="admin@abcedu.com"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Subscription Plan - Same as Tenant Creation */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b">Subscription Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Plan *
                      </label>
                      <select
                        value={formData.subscription_plan}
                        onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select Plan</option>
                        {subscriptionPlans.length === 0 ? (
                          <>
                            <option value="basic">Basic</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                          </>
                        ) : (
                          subscriptionPlans.map(plan => (
                            <option key={plan.id || plan.code} value={plan.code}>
                              {plan.name} 
                              {plan.max_colleges !== undefined && 
                                ` (${plan.max_colleges === -1 ? 'Unlimited' : plan.max_colleges} colleges)`
                              }
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Billing Cycle *
                      </label>
                      <div className="flex gap-3">
                        <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.billing_cycle === 'monthly' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}>
                          <input
                            type="radio"
                            name="billing_cycle_add"
                            value="monthly"
                            checked={formData.billing_cycle === 'monthly'}
                            onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="font-medium text-gray-800">Monthly</span>
                            {(() => {
                              const plan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                              if (plan?.price_monthly > 0) {
                                const gst = plan.gst_percent || 0;
                                const gstIncl = plan.gst_included || false;
                                let total = plan.price_monthly;
                                if (gst > 0 && !gstIncl) {
                                  total = Math.round(plan.price_monthly * (1 + gst/100));
                                }
                                return (
                                  <div>
                                    <p className="text-sm text-blue-600 font-bold">₹{total.toLocaleString('en-IN')}/mo</p>
                                    {gst > 0 && <p className="text-xs text-gray-500">{gstIncl ? `Incl.` : `+`} {gst}% GST</p>}
                                  </div>
                                );
                              }
                              return <p className="text-sm text-green-600">Free</p>;
                            })()}
                          </div>
                        </label>
                        
                        <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.billing_cycle === 'yearly' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}>
                          <input
                            type="radio"
                            name="billing_cycle_add"
                            value="yearly"
                            checked={formData.billing_cycle === 'yearly'}
                            onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="font-medium text-gray-800">Yearly</span>
                            {(() => {
                              const plan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                              if (plan?.price_yearly > 0) {
                                const gst = plan.gst_percent || 0;
                                const gstIncl = plan.gst_included || false;
                                let total = plan.price_yearly;
                                if (gst > 0 && !gstIncl) {
                                  total = Math.round(plan.price_yearly * (1 + gst/100));
                                }
                                const savings = plan.price_monthly > 0 
                                  ? Math.round((1 - plan.price_yearly/(plan.price_monthly*12))*100) 
                                  : 0;
                                return (
                                  <div>
                                    <p className="text-sm text-blue-600 font-bold">₹{total.toLocaleString('en-IN')}/yr</p>
                                    {gst > 0 && <p className="text-xs text-gray-500">{gstIncl ? `Incl.` : `+`} {gst}% GST</p>}
                                    {savings > 0 && <p className="text-xs text-green-600">Save {savings}%</p>}
                                  </div>
                                );
                              }
                              return <p className="text-sm text-green-600">Free</p>;
                            })()}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Selected Plan Summary */}
                  {formData.subscription_plan && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      {(() => {
                        const selectedPlan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                        if (!selectedPlan) return null;
                        
                        const basePrice = formData.billing_cycle === 'yearly' 
                          ? selectedPlan.price_yearly 
                          : selectedPlan.price_monthly;
                        
                        // GST Calculation
                        const gstPercent = selectedPlan.gst_percent || 0;
                        const gstIncluded = selectedPlan.gst_included || false;
                        let gstAmount = 0;
                        let totalPrice = basePrice || 0;
                        
                        if (gstPercent > 0 && basePrice > 0) {
                          if (gstIncluded) {
                            gstAmount = Math.round(basePrice - (basePrice / (1 + gstPercent/100)));
                          } else {
                            gstAmount = Math.round(basePrice * gstPercent / 100);
                            totalPrice = basePrice + gstAmount;
                          }
                        }
                        
                        return (
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-blue-900">{selectedPlan.name} Plan</h4>
                              <p className="text-sm text-blue-700">{selectedPlan.description}</p>
                              <div className="mt-2 text-xs text-gray-600">
                                <span>Max Colleges: {selectedPlan.max_colleges === -1 ? 'Unlimited' : selectedPlan.max_colleges}</span>
                                <span className="mx-2">•</span>
                                <span>Max Students: {selectedPlan.max_students === -1 ? 'Unlimited' : selectedPlan.max_students}</span>
                                <span className="mx-2">•</span>
                                <span>Max Staff: {selectedPlan.max_staff === -1 ? 'Unlimited' : selectedPlan.max_staff}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              {basePrice > 0 ? (
                                <>
                                  <p className="text-lg text-gray-600">
                                    ₹{basePrice.toLocaleString('en-IN')}
                                    {gstPercent > 0 && !gstIncluded && (
                                      <span className="text-sm"> + GST</span>
                                    )}
                                  </p>
                                  {gstPercent > 0 && (
                                    <p className="text-xs text-gray-500">
                                      GST ({gstPercent}%): ₹{gstAmount.toLocaleString('en-IN')}
                                    </p>
                                  )}
                                  <p className="text-2xl font-bold text-blue-600">
                                    ₹{totalPrice.toLocaleString('en-IN')}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Total per {formData.billing_cycle === 'yearly' ? 'year' : 'month'}
                                  </p>
                                </>
                              ) : (
                                <p className="text-2xl font-bold text-green-600">Free</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Additional Limits Override */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 text-gray-700">Custom Limits (Optional - Override Plan Defaults)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Colleges</label>
                      <input
                        type="number"
                        value={formData.max_colleges}
                        onChange={(e) => setFormData({...formData, max_colleges: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="From plan"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Students (-1=Unlimited)</label>
                      <input
                        type="number"
                        value={formData.max_students}
                        onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="From plan"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Staff (-1=Unlimited)</label>
                      <input
                        type="number"
                        value={formData.max_staff}
                        onChange={(e) => setFormData({...formData, max_staff: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="From plan"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t flex gap-3">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Creating...' : 'Create Group'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Edit Group</h2>
              <p className="text-sm text-gray-500">{selectedGroup.name}</p>
            </div>
            <form onSubmit={handleEditGroup}>
              <div className="p-6 space-y-4">
                {/* Logo Upload */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    {logoPreview || formData.logo ? (
                      <img src={logoPreview || formData.logo} alt="Logo" className="h-20 w-20 object-contain rounded-lg border" />
                    ) : (
                      <div className="h-20 w-20 rounded-lg border-2 border-dashed flex items-center justify-center text-gray-400">
                        <Image className="h-8 w-8" />
                      </div>
                    )}
                    <label className="absolute bottom-0 right-0 bg-blue-600 p-1 rounded-full cursor-pointer">
                      <Upload className="h-3 w-3 text-white" />
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p className="font-medium">Group Logo</p>
                    <p>Max 2MB, PNG/JPG</p>
                  </div>
                </div>

                {/* Group Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Group Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Group Code</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border rounded-lg uppercase"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                  />
                </div>

                {/* Contact Details */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Contact Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Person</label>
                      <input
                        type="text"
                        value={formData.contact_person}
                        onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Email</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Contact Phone</label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Subscription Plan - Same as Tenant Creation */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b">Subscription Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Plan *
                      </label>
                      <select
                        value={formData.subscription_plan}
                        onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select Plan</option>
                        {subscriptionPlans.length === 0 ? (
                          <>
                            <option value="basic">Basic</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                          </>
                        ) : (
                          subscriptionPlans.map(plan => (
                            <option key={plan.id || plan.code} value={plan.code}>
                              {plan.name} 
                              {plan.max_colleges !== undefined && 
                                ` (${plan.max_colleges === -1 ? 'Unlimited' : plan.max_colleges} colleges)`
                              }
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Billing Cycle *
                      </label>
                      <div className="flex gap-3">
                        <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.billing_cycle === 'monthly' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}>
                          <input
                            type="radio"
                            name="billing_cycle_edit"
                            value="monthly"
                            checked={formData.billing_cycle === 'monthly'}
                            onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="font-medium text-gray-800">Monthly</span>
                            {(() => {
                              const plan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                              if (plan?.price_monthly > 0) {
                                return <p className="text-sm text-blue-600 font-bold">₹{plan.price_monthly.toLocaleString('en-IN')}/mo</p>;
                              }
                              return <p className="text-sm text-green-600">Free</p>;
                            })()}
                          </div>
                        </label>
                        
                        <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.billing_cycle === 'yearly' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}>
                          <input
                            type="radio"
                            name="billing_cycle_edit"
                            value="yearly"
                            checked={formData.billing_cycle === 'yearly'}
                            onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="font-medium text-gray-800">Yearly</span>
                            {(() => {
                              const plan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                              if (plan?.price_yearly > 0) {
                                const savings = plan.price_monthly > 0 
                                  ? Math.round((1 - plan.price_yearly/(plan.price_monthly*12))*100) 
                                  : 0;
                                return (
                                  <div>
                                    <p className="text-sm text-blue-600 font-bold">₹{plan.price_yearly.toLocaleString('en-IN')}/yr</p>
                                    {savings > 0 && <p className="text-xs text-green-600">Save {savings}%</p>}
                                  </div>
                                );
                              }
                              return <p className="text-sm text-green-600">Free</p>;
                            })()}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Selected Plan Summary */}
                  {formData.subscription_plan && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      {(() => {
                        const selectedPlan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                        if (!selectedPlan) return null;
                        
                        const basePrice = formData.billing_cycle === 'yearly' 
                          ? selectedPlan.price_yearly 
                          : selectedPlan.price_monthly;
                        
                        // GST Calculation
                        const gstPercent = selectedPlan.gst_percent || 0;
                        const gstIncluded = selectedPlan.gst_included || false;
                        let gstAmount = 0;
                        let totalPrice = basePrice || 0;
                        
                        if (gstPercent > 0 && basePrice > 0) {
                          if (gstIncluded) {
                            gstAmount = Math.round(basePrice - (basePrice / (1 + gstPercent/100)));
                          } else {
                            gstAmount = Math.round(basePrice * gstPercent / 100);
                            totalPrice = basePrice + gstAmount;
                          }
                        }
                        
                        return (
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-blue-900">{selectedPlan.name} Plan</h4>
                              <p className="text-sm text-blue-700">{selectedPlan.description}</p>
                              <div className="mt-2 text-xs text-gray-600">
                                <span>Max Colleges: {selectedPlan.max_colleges === -1 ? 'Unlimited' : selectedPlan.max_colleges}</span>
                                <span className="mx-2">•</span>
                                <span>Max Students: {selectedPlan.max_students === -1 ? 'Unlimited' : selectedPlan.max_students}</span>
                                <span className="mx-2">•</span>
                                <span>Max Staff: {selectedPlan.max_staff === -1 ? 'Unlimited' : selectedPlan.max_staff}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              {basePrice > 0 ? (
                                <>
                                  <p className="text-lg text-gray-600">
                                    ₹{basePrice.toLocaleString('en-IN')}
                                    {gstPercent > 0 && !gstIncluded && (
                                      <span className="text-sm"> + GST</span>
                                    )}
                                  </p>
                                  {gstPercent > 0 && (
                                    <p className="text-xs text-gray-500">
                                      GST ({gstPercent}%): ₹{gstAmount.toLocaleString('en-IN')}
                                    </p>
                                  )}
                                  <p className="text-2xl font-bold text-blue-600">
                                    ₹{totalPrice.toLocaleString('en-IN')}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Total per {formData.billing_cycle === 'yearly' ? 'year' : 'month'}
                                  </p>
                                </>
                              ) : (
                                <p className="text-2xl font-bold text-green-600">Free</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Additional Limits & End Date */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 text-gray-700">Custom Limits (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Subscription End Date</label>
                      <input
                        type="date"
                        value={formData.subscription_end_date}
                        onChange={(e) => setFormData({...formData, subscription_end_date: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Colleges</label>
                      <input
                        type="number"
                        value={formData.max_colleges}
                        onChange={(e) => setFormData({...formData, max_colleges: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                        placeholder="From plan"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Students (-1=Unlimited)</label>
                      <input
                        type="number"
                        value={formData.max_students}
                        onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="From plan"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Staff (-1=Unlimited)</label>
                      <input
                        type="number"
                        value={formData.max_staff}
                        onChange={(e) => setFormData({...formData, max_staff: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="From plan"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t flex gap-3">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedGroup(null); resetForm(); }}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
